import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixQuickpagesPagesV1PagesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/quick-pages/v1/pages',
        destPath: '/v1/pages',
      },
      {
        srcPath: '/_api/quick-pages/v1/bulk/pages',
        destPath: '/v1/bulk/pages',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/quick-pages/v1/pages',
        destPath: '/v1/pages',
      },
      {
        srcPath: '/quick-pages/v1/bulk/pages',
        destPath: '/v1/bulk/pages',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_quick-pages_pages';

/** Creates a Page. */
export function createPage(payload: object): RequestOptionsFactory<any> {
  function __createPage({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'page.createdDate' },
          { path: 'page.updatedDate' },
          { path: 'page.publishedDate' },
          { path: 'page.unpublishedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.CreatePage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPage;
}

/** Retrieves a Page. */
export function getPage(payload: object): RequestOptionsFactory<any> {
  function __getPage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'GET' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.GetPage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPage;
}

/**
 * Updates a Page.
 *
 *
 * Each time the page is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the page.
 * This ensures you're working with the latest page
 * and prevents unintended overwrites.
 */
export function updatePage(payload: object): RequestOptionsFactory<any> {
  function __updatePage({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'page.createdDate' },
          { path: 'page.updatedDate' },
          { path: 'page.publishedDate' },
          { path: 'page.unpublishedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'PATCH' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.UpdatePage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{page.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePage;
}

/** Add a route for a page */
export function setPageRoute(payload: object): RequestOptionsFactory<any> {
  function __setPageRoute({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'PATCH' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.SetPageRoute',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/routes',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __setPageRoute;
}

/** Remove a route for a page */
export function removePageRoute(payload: object): RequestOptionsFactory<any> {
  function __removePageRoute({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'DELETE' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.RemovePageRoute',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/routes',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __removePageRoute;
}

/** Publish a Page. */
export function publishPage(payload: object): RequestOptionsFactory<any> {
  function __publishPage({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'unpublishedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.PublishPage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/publish',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishPage;
}

/** Unpublish a page. */
export function unpublishPage(payload: object): RequestOptionsFactory<any> {
  function __unpublishPage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.UnpublishPage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/unpublish',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unpublishPage;
}

/** Archive a Page. */
export function archivePage(payload: object): RequestOptionsFactory<any> {
  function __archivePage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.ArchivePage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/archive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __archivePage;
}

/** Unarchive a Page. */
export function unarchivePage(payload: object): RequestOptionsFactory<any> {
  function __unarchivePage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.UnarchivePage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/unarchive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unarchivePage;
}

/** Schedule a Page for publishing at a future date. */
export function schedulePage(payload: object): RequestOptionsFactory<any> {
  function __schedulePage({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'publishedDate' }, { path: 'unpublishedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.SchedulePage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}/schedule',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'page.createdDate' },
              { path: 'page.updatedDate' },
              { path: 'page.publishedDate' },
              { path: 'page.unpublishedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __schedulePage;
}

/** Deletes a Page. */
export function deletePage(payload: object): RequestOptionsFactory<any> {
  function __deletePage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'DELETE' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.DeletePage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/{pageId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deletePage;
}

/**
 * Retrieves a list of Pages, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 Pages can be returned per request.
 *
 * To learn how to query Pages, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 */
export function queryPages(payload: object): RequestOptionsFactory<any> {
  function __queryPages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'GET' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.QueryPages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/pages/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pages.createdDate' },
              { path: 'pages.updatedDate' },
              { path: 'pages.publishedDate' },
              { path: 'pages.unpublishedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixQuickpagesPagesV1PagesUrl({
            protoPath: '/v1/pages/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryPages;
}

/**
 * Synchronously update tags on multiple Pages, by list of Pages ids
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 */
export function bulkUpdatePageTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePageTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.BulkUpdatePageTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/bulk/pages/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdatePageTags;
}

/**
 * Asynchronously update tags on multiple Pages, by provided filter
 * An empty filter will update all Pages
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 */
export function bulkUpdatePageTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePageTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.quickpages.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.quickpages.pages.v1.Pages.BulkUpdatePageTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixQuickpagesPagesV1PagesUrl({
        protoPath: '/v1/bulk/pages/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdatePageTagsByFilter;
}
